import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { fileUrl } from "@/lib/api";
import { brl } from "@/lib/fmt";

export function FreeShipBar({ pricing }) {
  if (!pricing) return null;
  const pct = Math.min(100, ((pricing.frete_gratis_acima - pricing.falta_frete_gratis) / pricing.frete_gratis_acima) * 100);
  return (
    <div className="rounded-2xl bg-na-sage2 p-4" data-testid="free-shipping-bar">
      <p className="flex items-center gap-2 text-sm text-na-ink">
        <Truck className="h-4 w-4 text-na-forest" />
        {pricing.falta_frete_gratis > 0 ? <>Faltam <b>{brl(pricing.falta_frete_gratis)}</b> para o frete grátis</> : <b>Você ganhou frete grátis (PAC)</b>}
      </p>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-na-forest transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function CartLines({ compact }) {
  const { items, pricing, setQty, remove } = useStore();
  const lines = pricing?.itens || [];
  return (
    <ul className="divide-y divide-na-line">
      {items.map((it, idx) => {
        const l = lines.find((x) => x.slug === it.slug && x.frascos === it.frascos && x.assinatura === !!it.assinatura);
        return (
          <li key={`${it.slug}-${it.frascos}-${it.assinatura}`} className="flex gap-3 py-4" data-testid={`cart-line-${it.slug}-${it.frascos}`}>
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-na-sage2"><img src={fileUrl(l?.imagem)} alt="" className="prod-img h-14 object-contain" /></div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{l?.nome || it.slug}</p>
              <p className="text-xs text-na-ink2">{it.frascos} frasco{it.frascos > 1 ? "s" : ""}{l?.desconto_frascos_pct ? ` · ${l.desconto_frascos_pct}% off` : ""}{it.assinatura ? " · assinatura" : ""}</p>
              <div className="mt-2 flex items-center gap-2">
                <button aria-label="Diminuir" onClick={() => setQty(idx, it.quantidade - 1)} className="rounded-full border border-na-line p-1" data-testid={`qty-minus-${it.slug}`}><Minus className="h-3 w-3" /></button>
                <span className="w-6 text-center text-sm" data-testid={`qty-${it.slug}`}>{it.quantidade}</span>
                <button aria-label="Aumentar" onClick={() => setQty(idx, it.quantidade + 1)} className="rounded-full border border-na-line p-1" data-testid={`qty-plus-${it.slug}`}><Plus className="h-3 w-3" /></button>
                {!compact && <button aria-label="Remover" onClick={() => remove(idx)} className="ml-2 text-na-ink2 hover:text-red-700" data-testid={`remove-${it.slug}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
            <p className="text-sm font-semibold">{l ? brl(l.total) : "..."}</p>
          </li>
        );
      })}
    </ul>
  );
}

export function Totals({ pricing }) {
  if (!pricing) return null;
  return (
    <div className="space-y-1.5 text-sm" data-testid="cart-totals">
      <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{brl(pricing.subtotal)}</span></div>
      {pricing.descontos.frascos > 0 && <div className="flex justify-between text-na-acai"><span>Leve mais, pague menos</span><span>-{brl(pricing.descontos.frascos)}</span></div>}
      {pricing.descontos.assinatura > 0 && <div className="flex justify-between text-na-acai"><span>Assinatura</span><span>-{brl(pricing.descontos.assinatura)}</span></div>}
      {pricing.descontos.cupom > 0 && <div className="flex justify-between text-na-acai"><span>Cupom {pricing.cupom}</span><span>-{brl(pricing.descontos.cupom)}</span></div>}
      <div className="flex justify-between pt-1 text-base font-bold"><span>No Pix</span><span className="text-na-forest" data-testid="cart-pix-total">{brl(pricing.total_pix)}</span></div>
      <p className="text-right text-xs text-na-ink2">ou {brl(pricing.total_cartao)} em até {pricing.parcelas.vezes}x de {brl(pricing.parcelas.valor)} sem juros</p>
    </div>
  );
}

export default function CartDrawer() {
  const { drawer, setDrawer, items, pricing } = useStore();
  const nav = useNavigate();
  return (
    <Sheet open={drawer} onOpenChange={setDrawer}>
      <SheetContent className="flex w-full flex-col bg-na-bg sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader><SheetTitle className="font-serif text-2xl font-normal">Sua sacola</SheetTitle></SheetHeader>
        {!items.length ? (
          <p className="py-10 text-center text-na-ink2" data-testid="cart-empty">Sua sacola está vazia.</p>
        ) : (
          <>
            <FreeShipBar pricing={pricing} />
            <div className="-mx-2 flex-1 overflow-y-auto px-2"><CartLines /></div>
            <Totals pricing={pricing} />
            {pricing?.avisos?.map((a) => <p key={a} className="text-xs text-amber-700">{a}</p>)}
          </>
        )}
        <div className="mt-2 grid gap-2">
          {items.length > 0 && <button className="btn-na w-full" onClick={() => { setDrawer(false); nav("/checkout"); }} data-testid="drawer-checkout-button">Fechar pedido</button>}
          <button className="btn-line w-full" onClick={() => setDrawer(false)} data-testid="drawer-continue-button">Continuar comprando</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
